import React from 'react';
import {
  FlaskConical,
  Lock,
  Zap,
  AlertTriangle,
  Shield,
  Sparkles,
  ToggleLeft,
} from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';

/**
 * Advanced / Labs Section
 * 
 * Experimental features and advanced settings - restricted access.
 */
const AdvancedLabsSection = ({ agency, agencyId, isPlatformAdmin, isReadOnly }) => {
  // Only platform admins can see this section
  if (!isPlatformAdmin) {
    return (
      <div className="space-y-6" data-testid="labs-section-restricted">
        <div className="border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <FlaskConical className="text-slate-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Advanced / Labs</h2>
              <p className="text-sm text-slate-500">
                Experimental features
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Lock className="text-slate-400" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Restricted Access
          </h3>
          <p className="text-slate-500 mb-4 max-w-md mx-auto">
            Advanced and experimental features are only available to platform administrators.
          </p>
          <Badge className="bg-slate-200 text-slate-600 border-0">
            <Shield size={12} className="mr-1" />
            Admin Only
          </Badge>
        </div>
      </div>
    );
  }
  
  const labsFeatures = [
    {
      id: 'ai_auto_analysis',
      name: 'AI Auto-Analysis',
      description: 'Automatically analyze imported credit reports using AI',
      status: 'beta',
      enabled: true,
    },
    {
      id: 'smart_dispute_routing',
      name: 'Smart Dispute Routing',
      description: 'AI-powered dispute letter template selection',
      status: 'alpha',
      enabled: false,
    },
    {
      id: 'predictive_scoring',
      name: 'Predictive Score Improvement',
      description: 'Predict potential credit score improvements from disputes',
      status: 'experimental',
      enabled: false,
    },
    {
      id: 'bulk_processor',
      name: 'Bulk Processing V2',
      description: 'Next-generation bulk import and processing engine',
      status: 'beta',
      enabled: true,
    },
  ];
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'beta':
        return <Badge className="bg-blue-100 text-blue-700">Beta</Badge>;
      case 'alpha':
        return <Badge className="bg-purple-100 text-purple-700">Alpha</Badge>;
      case 'experimental':
        return <Badge className="bg-amber-100 text-amber-700">Experimental</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };
  
  return (
    <div className="space-y-6" data-testid="labs-section">
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <FlaskConical className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Advanced / Labs</h2>
              <p className="text-sm text-slate-500">
                Experimental features and advanced configuration
              </p>
            </div>
          </div>
          
          <Badge className="bg-amber-100 text-amber-700 border-0">
            <Sparkles size={12} className="mr-1" />
            Platform Admin
          </Badge>
        </div>
      </div>
      
      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="text-amber-600 mt-0.5" size={18} />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Experimental Features</p>
          <p className="text-amber-700 mt-1">
            These features are in development and may change or be removed. 
            Use with caution in production environments.
          </p>
        </div>
      </div>
      
      {/* Labs Features */}
      <div className="space-y-3">
        {labsFeatures.map((feature) => (
          <div 
            key={feature.id}
            className="bg-white rounded-xl border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Zap size={18} className="text-purple-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{feature.name}</h3>
                    {getStatusBadge(feature.status)}
                  </div>
                  <p className="text-sm text-slate-500">{feature.description}</p>
                </div>
              </div>
              <Switch 
                checked={feature.enabled} 
                disabled={isReadOnly}
              />
            </div>
          </div>
        ))}
      </div>
      
      {/* Advanced Config */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          <ToggleLeft size={14} className="inline mr-1" />
          Advanced Configuration
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900 text-sm">Debug Mode</p>
              <p className="text-xs text-slate-500">Enable verbose logging for this agency</p>
            </div>
            <Switch disabled={isReadOnly} />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900 text-sm">Bypass Rate Limits</p>
              <p className="text-xs text-slate-500">Remove API rate limiting for testing</p>
            </div>
            <Switch disabled={isReadOnly} />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900 text-sm">Feature Flags Override</p>
              <p className="text-xs text-slate-500">Allow manual feature flag configuration</p>
            </div>
            <Switch disabled={isReadOnly} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedLabsSection;
